/**
 * ScrollToTop — global route-change scroll reset.
 *
 * React Router preserves scroll position by default when you navigate
 * between pages — that's why some pages started "in the middle" instead
 * of at the header. This component runs once per route change and snaps
 * the viewport back to the top.
 *
 * Mount inside <BrowserRouter> but OUTSIDE <Routes> so it doesn't unmount
 * when routes change.
 *
 * Respects:
 *   - URL hash anchors (#section) — if a hash is present, scroll to that
 *     element instead of the top.
 *   - Browser back/forward — we let those use the native scroll restoration.
 */
import { useEffect } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  const navType = useNavigationType(); // 'POP' for back/forward

  useEffect(() => {
    // Don't override the browser's restored scroll when user hits Back/Forward
    if (navType === 'POP') return;

    // If the URL has a hash like /page#somesection, try to scroll to that element
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        el.scrollIntoView({ behavior: 'auto', block: 'start' });
        return;
      }
    }

    // Default: snap to the top
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname, hash, navType]);

  return null;
};

export default ScrollToTop;
